import type { FC } from 'react'
import { ImageCard } from '@/components/commons/image-card'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { CheckIcon, ThumbsDownIcon } from '@/components/ui/icons'
import {
	SpaceImageFrame,
	SpaceImageSelectionInfo,
	SpaceImagesContainer
} from '@/features/photoset/components/space-images-atoms'
import { useImageSelectionApproval } from '@/features/photoset/queries/use-image-selection-approval'
import type { Image } from '@/features/photoset/types'
import { errorHandler } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

interface EditingQaSelectionSpaceImagesProps {
	images: Omit<Image, 'space'>[]
	photosetId: number
	spaceId: number
	isQaView: boolean
}

export const EditingQaSelectionSpaceImages: FC<
	EditingQaSelectionSpaceImagesProps
> = ({ images, photosetId, spaceId, isQaView }) => {
	const { t } = useTranslation()
	const { mutate: imageSelectionApprovalToggle } = useImageSelectionApproval({
		photosetId,
		photosetSpaceId: spaceId,
		options: {
			onError: (error) => errorHandler(error)
		}
	})

	const onToggleClick = (image: Omit<Image, 'space'>) => {
		imageSelectionApprovalToggle({
			imageId: image.id,
			curation_approved: !image.curation_approved
		})
	}

	return (
		<SpaceImagesContainer $gap={4}>
			{images.map((image) => (
				<SpaceImageFrame
					$isSelected={image.is_final_select}
					key={image.id}
				>
					<ImageCard
						endSlot={
							<SpaceImageSelectionInfo>
								<section className='flex w-full items-center justify-between gap-2'>
									<h4>{image.name}</h4>
									{image.is_final_select && (
										<div className='flex items-center justify-between gap-1 [&_svg]:fill-neutral-white'>
											<span className='flex h-[18px] w-[18px] items-center justify-center rounded-2xl bg-neutral-700'>
												<CheckIcon size={12} />
											</span>
											<p className='font-medium text-neutral-black text-xs'>
												{t('common:selected')}
											</p>
										</div>
									)}
								</section>
								<ButtonLegacy
									className='w-full'
									disabled={!isQaView}
									onClick={() => onToggleClick(image)}
									size={'sm'}
									type='button'
									variant={
										image.curation_approved
											? 'secondary'
											: 'secondaryDanger'
									}
								>
									<ThumbsDownIcon />
									{t(
										image.curation_approved
											? 'common:reject'
											: 'common:rejected'
									)}
								</ButtonLegacy>
							</SpaceImageSelectionInfo>
						}
						hideImageInfo
						image={image}
						isSelected={false}
						thumbnail={image.thumbnails?.xs}
					/>
				</SpaceImageFrame>
			))}
		</SpaceImagesContainer>
	)
}
